import { useState } from 'react';

import {
  GoToCommentsButton,
  GoToCommentsMenuItem,
} from '@/components/comments/GoToCommentsButton/GoToCommentsButton';
import { StoreResellerBadge } from '@/components/StoreResellerBadge';
import Button from '@/design_system/Button';
import Menu from '@/design_system/Menu';
import { Section, Separator } from '@/design_system/Menu/Menu';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconArrow from '@/icons/Arrow.svg';
import IconMore from '@/icons/More.svg';
import {
  isArchived,
  isInCompletedStep,
  type RequestWithRelations,
  useComments,
  useRequestDocuments,
} from '@/models/request';
import { useStore } from '@/models/store';
import { hasPermission, isExternalUserForOrganization } from '@/models/user';
import {
  ArchiveRequestButton,
  ArchiveRequestDialog,
  ArchiveRequestMenuItem,
} from '@/routes/Requests/components/ArchiveRequestButton';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import {
  DownloadInvoiceButton,
  DownloadInvoiceMenuItem,
  useDownloadInvoiceActions,
} from '@/routes/Requests/Request/components/RequestHeader/DownloadInvoiceButton';
import {
  PrintMenuItems,
  PrintsMenu,
} from '@/routes/Requests/Request/components/RequestHeader/PrintOptions';
import { useCurrentOrganization, useCurrentSession } from '@/services/auth';
import { getWorkshopsWithClientCommunication } from '@/utils/permission';
import { useRequestName } from '@/utils/useRequestName';
import useViewPort from '@/utils/useViewport';

import { ClientCommunicationButton } from './ClientCommunicationButton';
import {
  SubscriptionButton,
  SubscriptionMenuItem,
  useToggleSubscription,
} from './SubscriptionButton';

export const RequestHeader = ({
  request,
  onCommentButtonPress,
}: {
  request: RequestWithRelations;
  onCommentButtonPress: () => void;
}) => {
  const { isMobile } = useViewPort();
  const { currentSession } = useCurrentSession();
  const { workflow, iam } = useRequestContext();
  const { data: store } = useStore(request?.storeId);
  const [currentOrganization] = useCurrentOrganization();
  const { view } = useRequestContext();
  const requestName = useRequestName({ request, isClientEnabled: view.client.enabled });
  const isExternalUser = isExternalUserForOrganization(currentSession, request.organization.id);

  const { data: { comments } = { comments: [] } } = useComments({
    requestId: request.id,
  });

  // Improvement: Store the invoiceId on the request itself to check its existence
  const canDownloadInvoice =
    !iam.hasOnlyWorkshopScopeOnRequest &&
    hasPermission(currentSession, 'view_invoice', {
      organizationId: currentOrganization?.id,
    }) &&
    request.articles.some((article) => article.paidAt);

  const canArchive =
    !isArchived(request) &&
    hasPermission(currentSession, 'archive_article', { organizationId: request.organizationId });

  const { toggleSubscription, isSubscribeToRequestPending, isUnsubscribeFromRequestPending } =
    useToggleSubscription({
      request,
    });
  const [showArchivalConfirmation, setShowArchivalConfirmation] = useState(false);

  const { invoice } = useDownloadInvoiceActions({
    request,
    enabled: canDownloadInvoice,
  });

  const { data: generatedDocuments = [] } = useRequestDocuments({
    requestId: request.id,
  });
  const isServiceChosenForAllArticles = request.articles.every(
    (article) => !!article.serviceChosenAt
  );
  const showPrintItemsSheets = isServiceChosenForAllArticles;
  const showPrintDropoffReceipt =
    !!(request.client && request.store) &&
    !iam.hasOnlyWorkshopScopeOnRequest &&
    !!workflow?.config?.dropOffReceipt?.enabled;

  const hasDocumentsToPrint =
    showPrintItemsSheets || showPrintDropoffReceipt || !!generatedDocuments.length;
  const showPrintsButton =
    hasDocumentsToPrint && !(isArchived(request) && !isInCompletedStep(request));

  const hasViewClientCommunicationPermission = !!hasPermission(
    currentSession,
    'view_client_communication',
    [
      {
        organizationId: currentOrganization?.id,
        entityId: null,
      },
      {
        entityId: request.afterSalesId,
      },
      {
        entityId: request.store?.id,
      },
      ...getWorkshopsWithClientCommunication(request.articles),
    ]
  );

  const showClientCommunication =
    request.organization.config.clientCommunication?.chat?.enabled &&
    request.client &&
    hasViewClientCommunicationPermission;

  return (
    <>
      {isMobile && (
        <Button variant="secondary" size="large" to="/requests" iconOnly noBorder>
          <IconArrow left />
        </Button>
      )}

      <Stack gap={isMobile ? undefined : '0.25rem'} style={{ flex: 1, minWidth: '0' }}>
        {/* TODO: See with the design to avoid having headline on desktop and paragraph on mobile */}
        <Stack
          row
          gap="0.25rem"
          alignItems="center"
          className="headline-300-bold sentry-mask paragraph-100-medium-mobile text-ellipsis"
        >
          <span>[{request.reference}]</span>
          {view.information.externalReference.enabled && !!request.externalReference && (
            <span>{request.externalReference}</span>
          )}
          <span>{requestName.major}</span>
          <span>
            {!isExternalUser &&
              requestName.major === store?.name &&
              !!store?.organizationConfig?.external && <StoreResellerBadge size="large" />}
          </span>
        </Stack>

        {!!requestName.minor && (
          <Stack row gap="0.25rem" alignItems="center">
            <Paragraph size={isMobile ? '200' : '50'}>{requestName.minor}</Paragraph>
            {!isExternalUser &&
              requestName.minor === store?.name &&
              !!store?.organizationConfig?.external && <StoreResellerBadge />}
          </Stack>
        )}
      </Stack>

      {!isMobile && (
        <Stack row gap="0.5rem">
          {showClientCommunication && <ClientCommunicationButton />}
          <SubscriptionButton
            request={request}
            disabled={isUnsubscribeFromRequestPending || isSubscribeToRequestPending}
            onPress={() => {
              toggleSubscription();
            }}
          />
          <GoToCommentsButton
            onCommentButtonPress={onCommentButtonPress}
            nbOfComments={comments.length}
          />
          {showPrintsButton && (
            <PrintsMenu>
              <PrintMenuItems
                request={request}
                showPrintItemsSheets={showPrintItemsSheets}
                showPrintDropoffReceipt={showPrintDropoffReceipt}
                generatedDocuments={generatedDocuments}
              />
            </PrintsMenu>
          )}
          {canDownloadInvoice && <DownloadInvoiceButton request={request} />}
          {canArchive && <ArchiveRequestButton iconOnly request={request} size="large" />}
        </Stack>
      )}
      {isMobile && (
        <Stack row alignItems="center">
          {showClientCommunication && <ClientCommunicationButton />}
          <Menu
            placement="bottom end"
            trigger={
              <Button variant="secondary" size="large" iconOnly noBorder>
                <IconMore />
              </Button>
            }
            onAction={(id) => {
              switch (id) {
                case 'toggle-subscription':
                  toggleSubscription();
                  break;
                case 'go-to-comments':
                  onCommentButtonPress();
                  break;
                case 'archive':
                  setShowArchivalConfirmation(true);
                  break;
                default:
                  break;
              }
            }}
          >
            <Section>
              <SubscriptionMenuItem request={request} />
              <GoToCommentsMenuItem nbOfComments={comments.length} />
            </Section>
            {showPrintsButton && (
              <>
                <Separator />
                <Section>
                  <PrintMenuItems
                    request={request}
                    showPrintItemsSheets={showPrintItemsSheets}
                    showPrintDropoffReceipt={showPrintDropoffReceipt}
                    generatedDocuments={generatedDocuments}
                  />
                </Section>
              </>
            )}
            {canDownloadInvoice && !!invoice && (
              <>
                <Separator />
                <Section>
                  <DownloadInvoiceMenuItem invoice={invoice} />
                </Section>
              </>
            )}
            <Separator />
            <Section>
              <ArchiveRequestMenuItem />
            </Section>
          </Menu>
          <ArchiveRequestDialog
            request={request}
            onArchive={() => setShowArchivalConfirmation(false)}
            onArchiveError={() => setShowArchivalConfirmation(false)}
            showArchivalConfirmation={showArchivalConfirmation}
            setShowArchivalConfirmation={setShowArchivalConfirmation}
          />
        </Stack>
      )}
    </>
  );
};
